import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
  Snackbar,
} from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import SentimentDissatisfiedRoundedIcon from '@mui/icons-material/SentimentDissatisfiedRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { gamesApi, getSocket, onSocketJoin } from '../../../services/api/gamesApi';
import { authApi } from '../../../services/api/authApi';
import './WinGoScreen.css';

const FIXED_MULTIPLIER = 2;
const HISTORY_LIMIT = 11;

const GREEN_NUMBERS = new Set([1, 3, 7, 9]);

function ballTone(num) {
  return GREEN_NUMBERS.has(num) ? 'green' : 'red';
}

function ballClass(num, selected = false) {
  const tone = ballTone(num);
  return [
    'wingo-ball',
    `wingo-ball--${tone}`,
    selected ? 'wingo-ball--selected' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

function WinGoScreen({ user, onBack, onWalletUpdate }) {
  const [gameState, setGameState] = useState(null);
  const [walletBalance, setWalletBalance] = useState(user?.walletBalance ?? 0);
  const [selectedNumber, setSelectedNumber] = useState(5);
  const [selectedSize, setSelectedSize] = useState('Big');
  const [betAmountInput, setBetAmountInput] = useState('10');
  const [toast, setToast] = useState('');
  const [loading, setLoading] = useState(false);
  const [outcomePopup, setOutcomePopup] = useState(null);
  const shownOutcomeRef = useRef(null);

  const refreshUser = useCallback(async () => {
    try {
      const me = await authApi.me();
      setWalletBalance(me.walletBalance);
      onWalletUpdate?.(me);
    } catch {
      /* ignore */
    }
  }, [onWalletUpdate]);

  useEffect(() => {
    let cancelled = false;
    const refreshState = () =>
      gamesApi
        .wingoState()
        .then((state) => {
          if (!cancelled) setGameState(state);
        })
        .catch(() => {});

    refreshState();
    const pollTimer = setInterval(refreshState, 5000);

    const socket = getSocket();
    const join = () => socket.emit('join:wingo');
    const stopJoin = onSocketJoin(join);
    const onState = (state) => {
      setGameState(state);
      if (state.lastOutcome?.status === 'WON' || state.lastOutcome?.status === 'LOST') {
        refreshUser();
      }
    };
    socket.on('wingo:state', onState);

    return () => {
      cancelled = true;
      clearInterval(pollTimer);
      stopJoin();
      socket.off('wingo:state', onState);
    };
  }, [refreshUser]);

  const timeLeft = gameState?.timeLeft ?? 30;
  const roundSeconds = gameState?.roundSeconds ?? 30;
  const period = gameState?.period ?? '—';
  const myBet = gameState?.myBet;
  const lastOutcome = gameState?.lastOutcome;
  const historyRows = useMemo(
    () => (gameState?.history ?? []).slice(0, HISTORY_LIMIT),
    [gameState?.history],
  );
  const playerCount = gameState?.playerCount ?? 0;
  const maxPlayers = gameState?.maxPlayers ?? 500;
  const minBet = gameState?.minBet ?? 5;
  const maxBet = gameState?.maxBet ?? 5000;

  const isFinalCountdown = timeLeft > 0 && timeLeft <= 5;
  const countdownDigits = String(timeLeft).padStart(2, '0').split('');
  const balls = useMemo(() => Array.from({ length: 10 }, (_, i) => i), []);
  const timerProgress = ((roundSeconds - timeLeft) / roundSeconds) * 100;
  const bettingOpen = timeLeft > 5 && !myBet;

  useEffect(() => {
    if (!lastOutcome || (lastOutcome.status !== 'WON' && lastOutcome.status !== 'LOST')) return;
    const key = [
      lastOutcome.status,
      lastOutcome.resultNumber,
      lastOutcome.selectedNumber,
      lastOutcome.amount,
    ].join('-');
    if (shownOutcomeRef.current === key) return;
    shownOutcomeRef.current = key;
    setOutcomePopup(lastOutcome);
  }, [lastOutcome]);

  const closeOutcomePopup = () => setOutcomePopup(null);

  const pickSize = (size) => {
    setSelectedSize(size);
    setSelectedNumber(size === 'Small' ? 0 : 5);
  };

  const pickNumber = (num) => {
    setSelectedNumber(num);
    setSelectedSize(num <= 4 ? 'Small' : 'Big');
  };

  const placeBet = async () => {
    const amount = Number(betAmountInput);
    if (!Number.isFinite(amount) || amount <= 0) {
      setToast('Enter a valid bet amount');
      return;
    }
    if (amount < minBet || amount > maxBet) {
      setToast(`Bet must be between ₹${minBet} and ₹${maxBet}`);
      return;
    }
    if (amount > walletBalance) {
      setToast('Insufficient balance');
      return;
    }
    if (myBet) return;
    if (timeLeft <= 5) {
      setToast('Betting closed for this round');
      return;
    }
    setLoading(true);
    try {
      await gamesApi.wingoBet({
        amount,
        selectedNumber,
        selectedSize,
        multiplier: FIXED_MULTIPLIER,
      });
      setToast('Bet placed — good luck!');
      await refreshUser();
      setGameState(await gamesApi.wingoState());
    } catch (err) {
      setToast(err.message);
    } finally {
      setLoading(false);
    }
  };

  const potentialWin = Number(betAmountInput || 0) * FIXED_MULTIPLIER;
  const isOutcomeWin = outcomePopup?.status === 'WON';
  const numberMatched = outcomePopup?.resultNumber === outcomePopup?.selectedNumber;

  return (
    <Box className="wingo-root">
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} />

      <Dialog
        open={Boolean(outcomePopup)}
        onClose={closeOutcomePopup}
        className="wingo-outcome-dialog"
        PaperProps={{
          className: `wingo-outcome-dialog__paper${isOutcomeWin ? ' wingo-outcome-dialog__paper--win' : ' wingo-outcome-dialog__paper--lose'}`,
          sx: { bgcolor: '#ffffff', color: '#111827' },
        }}
      >
        {outcomePopup ? (
          <>
            {/* <IconButton
              className="wingo-outcome-dialog__close"
              onClick={closeOutcomePopup}
              aria-label="Close result"
              size="small"
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton> */}
            <DialogContent className="wingo-outcome-dialog__content">
              <Box className="wingo-outcome-dialog__top">
                <Box className="wingo-outcome-dialog__head">
                  <Box className={`wingo-outcome-dialog__icon${isOutcomeWin ? ' wingo-outcome-dialog__icon--win' : ''}`}>
                    {isOutcomeWin ? (
                      <EmojiEventsRoundedIcon sx={{ fontSize: 32 }} />
                    ) : (
                      <SentimentDissatisfiedRoundedIcon sx={{ fontSize: 32 }} />
                    )}
                  </Box>
                  <Box>
                    <Typography className="wingo-outcome-dialog__title">
                      {isOutcomeWin ? 'You Won!' : 'You Lost'}
                    </Typography>
                    <Typography className="wingo-outcome-dialog__subtitle">
                      Round result is in
                    </Typography>
                  </Box>
                </Box>

                <Box className="wingo-outcome-dialog__result-ball">
                  <span className={`wingo-outcome-dialog__ball wingo-outcome-dialog__ball--${ballTone(outcomePopup.resultNumber)}`}>
                    {outcomePopup.resultNumber}
                  </span>
                  <Typography className="wingo-outcome-dialog__result-meta">
                    {outcomePopup.resultSize} · {ballTone(outcomePopup.resultNumber) === 'green' ? 'Green' : 'Red'}
                  </Typography>
                </Box>
              </Box>

              <Box className="wingo-outcome-dialog__details">
                <Box className="wingo-outcome-dialog__detail-row">
                  <Typography>Your pick</Typography>
                  <Typography>
                    #{outcomePopup.selectedNumber} · {outcomePopup.selectedSize}
                  </Typography>
                </Box>
                {numberMatched ? (
                  <Typography className="wingo-outcome-dialog__match-note">
                    Exact number match!
                  </Typography>
                ) : null}
                <Box className="wingo-outcome-dialog__detail-row">
                  <Typography>{isOutcomeWin ? 'You received' : 'Bet amount'}</Typography>
                  <Typography className={isOutcomeWin ? 'wingo-outcome-dialog__amount--win' : 'wingo-outcome-dialog__amount--lose'}>
                    {isOutcomeWin
                      ? `₹${outcomePopup.payout.toFixed(2)}`
                      : `−₹${outcomePopup.amount.toFixed(2)}`}
                  </Typography>
                </Box>
              
              </Box>

              <Button
                fullWidth
                variant="contained"
                className="wingo-outcome-dialog__btn"
                onClick={closeOutcomePopup}
              >
                Continue
              </Button>
            </DialogContent>
          </>
        ) : null}
      </Dialog>

      <Box className="wingo-header">
        <IconButton size="small" onClick={onBack} className="wingo-back-btn">
          <ArrowBackIosNewRoundedIcon fontSize="small" />
        </IconButton>
        <Box>
          <Typography className="wingo-title">Win Go</Typography>
          <Typography className="wingo-subtitle">
            <span className="wingo-live-dot" aria-hidden />
            Live · {roundSeconds}s rounds
          </Typography>
        </Box>
        <Chip
          icon={<AccountBalanceWalletRoundedIcon sx={{ fontSize: '16px !important' }} />}
          label={`₹${walletBalance.toFixed(0)}`}
          className="wingo-wallet-chip"
        />
      </Box>

      <Paper elevation={0} className="wingo-hero-card">
        <Box className="wingo-hero-glow" aria-hidden />
        <Box className="wingo-hero-top">
          <Box>
            <Typography className="wingo-hero-label">Round timer</Typography>
            <Typography className={`wingo-timer${timeLeft <= 10 ? ' wingo-timer--urgent' : ''}`}>
              {String(timeLeft).padStart(2, '0')}
              <span className="wingo-timer-unit">s</span>
            </Typography>
          </Box>
          <Box className="wingo-hero-meta">
            <Box className="wingo-meta-pill">#{period.slice(-6)}</Box>
            <Typography className="wingo-meta-players">
              {playerCount}/{maxPlayers} players
            </Typography>
          </Box>
        </Box>
        <LinearProgress
          variant="determinate"
          value={timerProgress}
          className={`wingo-timer-bar${timeLeft <= 10 ? ' wingo-timer-bar--urgent' : ''}`}
        />
        {historyRows.length > 0 && (
          <Box className="wingo-recent-strip">
            <Typography className="wingo-recent-label">Recent</Typography>
            {historyRows.map((row) => (
              <Box
                key={row.period}
                className={`wingo-recent-ball wingo-recent-ball--${ballTone(row.resultNumber)}`}
                title={`${row.resultNumber} (${row.resultSize})`}
              >
                {row.resultNumber}
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      <Paper elevation={0} className="wingo-section">
        <Typography className="wingo-section-title">Pick Big or Small</Typography>
        <Stack direction="row" spacing={1.5} className="wingo-size-row">
          <Button
            fullWidth
            className={`wingo-size-btn small ${selectedSize === 'Small' ? 'active' : ''}`}
            onClick={() => pickSize('Small')}
            disabled={!!myBet}
          >
            <span className="wingo-size-icon" aria-hidden>🌊</span>
            <Typography className="wingo-size-label">Small</Typography>
            <Typography className="wingo-size-nums">0 – 4</Typography>
          </Button>
          <Button
            fullWidth
            className={`wingo-size-btn big ${selectedSize === 'Big' ? 'active' : ''}`}
            onClick={() => pickSize('Big')}
            disabled={!!myBet}
          >
            <span className="wingo-size-icon" aria-hidden>🔥</span>
            <Typography className="wingo-size-label">Big</Typography>
            <Typography className="wingo-size-nums">5 – 9</Typography>
          </Button>
        </Stack>

        <Typography className="wingo-section-title wingo-section-title--spaced">Lucky number (0–9)</Typography>
        <Box className="wingo-lucky-box">
          {isFinalCountdown && (
            <Box className="wingo-countdown-overlay wingo-countdown-overlay--lucky">
              <Box className="wingo-countdown-modal">
                {countdownDigits.map((digit, index) => (
                  <Box key={`countdown-digit-${index}`} className="wingo-countdown-digit">{digit}</Box>
                ))}
              </Box>
              <Typography className="wingo-countdown-label">Drawing result…</Typography>
            </Box>
          )}
          <Box className="wingo-balls-grid">
            {balls.map((num) => (
              <button
                key={num}
                type="button"
                className={ballClass(num, selectedNumber === num)}
                onClick={() => pickNumber(num)}
                disabled={!!myBet}
              >
                <span className="wingo-ball-shine" aria-hidden />
                {num}
              </button>
            ))}
          </Box>
        </Box>
      </Paper>

      <Paper elevation={0} className="wingo-section wingo-bet-card">
        <Typography className="wingo-section-title">Place your bet</Typography>
        <Box className="wingo-fixed-multi">
          <Typography className="wingo-fixed-multi-label">Payout multiplier</Typography>
          <Typography className="wingo-fixed-multi-value">2×</Typography>
        </Box>
        <TextField
          label="Bet amount (₹)"
          type="text"
          inputMode="decimal"
          size="small"
          value={betAmountInput}
          onChange={(e) => setBetAmountInput(e.target.value.replace(/[^\d.]/g, ''))}
          placeholder={`${minBet} - ${maxBet}`}
          fullWidth
          className="wingo-amount-field"
          disabled={!!myBet}
          helperText={`Min ₹${minBet} · Max ₹${maxBet}`}
        />
        <Box className="wingo-slip">
          <Typography>Your pick</Typography>
          <Typography className="wingo-slip-value">
            {selectedSize} · #{selectedNumber} · 2x
          </Typography>
          <Typography className="wingo-slip-total-label">Potential win</Typography>
          <Typography className="wingo-slip-total">₹{potentialWin.toFixed(2)}</Typography>
        </Box>
        <Button
          variant="contained"
          fullWidth
          className="wingo-submit-btn"
          onClick={placeBet}
          disabled={!bettingOpen || loading}
        >
          {myBet
            ? `Locked — ${myBet.selectedSize} #${myBet.selectedNumber} · ₹${myBet.amount}`
            : loading
              ? 'Placing…'
              : timeLeft <= 5
                ? 'Betting closed'
                : `Bet ₹${Number(betAmountInput || 0).toFixed(0)}`}
        </Button>
      </Paper>

      <Paper elevation={0} className="wingo-history-card">
        <Typography className="wingo-section-title">Last {HISTORY_LIMIT} results</Typography>
        <Box className="wingo-table-head wingo-table-head--4">
          <Typography>Period</Typography>
          <Typography>Num</Typography>
          <Typography>Size</Typography>
          <Typography>Color</Typography>
        </Box>
        {historyRows.map((row) => (
          <Box key={row.period} className="wingo-table-row wingo-table-row--4">
            <Typography className="wingo-period-cell">{row.period.slice(-6)}</Typography>
            <Typography className="wingo-result-number">{row.resultNumber}</Typography>
            <Typography>
              <span className={`wingo-size-badge ${row.resultSize === 'Big' ? 'wingo-size-badge--big' : 'wingo-size-badge--small'}`}>
                {row.resultSize}
              </span>
            </Typography>
            <Typography>
              <span className={`wingo-color-badge wingo-color-badge--${ballTone(row.resultNumber)}`}>
                {row.resultColor}
              </span>
            </Typography>
          </Box>
        ))}
      </Paper>
    </Box>
  );
}

export default WinGoScreen;
